import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";
import {
  FileText, Plus, Upload, Link as LinkIcon, ExternalLink, Edit2, Trash2,
  Calendar as CalendarIcon, Archive, CheckCircle2, AlertCircle,
} from "lucide-react";

const CATEGORIES = [
  { id: "locazione", label: "Locazione", color: "bg-rose-100 text-rose-800 border-rose-300" },
  { id: "fornitore", label: "Fornitore", color: "bg-sky-100 text-sky-800 border-sky-300" },
  { id: "manutenzione", label: "Manutenzione", color: "bg-teal-100 text-teal-800 border-teal-300" },
  { id: "assicurazione", label: "Assicurazione", color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  { id: "lavoro", label: "Lavoro", color: "bg-amber-100 text-amber-800 border-amber-300" },
  { id: "consulenza", label: "Consulenza", color: "bg-violet-100 text-violet-800 border-violet-300" },
  { id: "altro", label: "Altro", color: "bg-slate-100 text-slate-800 border-slate-300" },
];

const EMPTY_FORM = {
  id: "",
  title: "",
  category: "altro",
  counterparty: "",
  start_date: "",
  end_date: "",
  amount: "",
  notes: "",
  file_url: "",
  file_name: "",
  auto_create_deadline: true,
};

export default function ContractsTab() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("active");
  const [uploading, setUploading] = useState(false);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== "all") params.category = filterCategory;
      if (filterStatus !== "all") params.status = filterStatus;
      const res = await api.get("/admin/contracts", { params });
      setContracts(res.data.contracts || []);
    } catch (err) {
      toast.error(errMsg(err, "Errore caricamento contratti"));
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterStatus]);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const openCreate = () => { setForm(EMPTY_FORM); setDialogOpen(true); };

  const openEdit = (c) => {
    setForm({
      id: c.id,
      title: c.title || "",
      category: c.category || "altro",
      counterparty: c.counterparty || "",
      start_date: c.start_date || "",
      end_date: c.end_date || "",
      amount: c.amount != null ? String(c.amount) : "",
      notes: c.notes || "",
      file_url: c.file_url || "",
      file_name: c.file_name || "",
      auto_create_deadline: !!c.linked_deadline_id,
    });
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/contracts/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, file_url: res.data.url, file_name: file.name }));
      toast.success(`✓ ${file.name} caricato`);
    } catch (err) {
      toast.error(errMsg(err, "Errore upload"));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title?.trim()) { toast.error("Inserisci il titolo del contratto"); return; }
    if (!form.file_url && !form.file_name) { toast.error("Carica un file o incolla un link"); return; }
    const payload = {
      title: form.title.trim(),
      category: form.category,
      counterparty: form.counterparty.trim(),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      amount: form.amount ? parseFloat(form.amount) : null,
      notes: form.notes.trim(),
      file_url: form.file_url,
      file_name: form.file_name,
      auto_create_deadline: form.auto_create_deadline,
    };
    try {
      if (form.id) {
        await api.put(`/admin/contracts/${form.id}`, payload);
        toast.success("✓ Contratto aggiornato");
      } else {
        await api.post("/admin/contracts", payload);
        toast.success("✓ Contratto salvato" + (form.end_date && form.auto_create_deadline ? " · Scadenza collegata creata automaticamente" : ""));
      }
      setDialogOpen(false);
      fetchContracts();
    } catch (err) {
      toast.error(errMsg(err, "Errore salvataggio"));
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Eliminare il contratto "${c.title}"? Verrà rimossa anche la scadenza collegata.`)) return;
    try {
      await api.delete(`/admin/contracts/${c.id}`);
      toast.success("Contratto eliminato");
      fetchContracts();
    } catch (err) {
      toast.error(errMsg(err, "Errore eliminazione"));
    }
  };

  const handleArchive = async (c) => {
    try {
      await api.patch(`/admin/contracts/${c.id}/status?status=archived`);
      toast.success("Contratto archiviato");
      fetchContracts();
    } catch (err) {
      toast.error(errMsg(err, "Errore archiviazione"));
    }
  };

  const getCategoryStyle = (cat) => CATEGORIES.find((c) => c.id === cat)?.color || CATEGORIES[6].color;
  const getCategoryLabel = (cat) => CATEGORIES.find((c) => c.id === cat)?.label || "Altro";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-[#1E3A5F] flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#AC5D45]" />
            Contratti
          </h2>
          <p className="text-sm text-[#635F59] mt-1">
            Archivio contratti attivi e scaduti. Le scadenze vengono create automaticamente 30 giorni prima della fine del contratto.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="contract-new-btn">
          <Plus className="w-4 h-4 mr-2" />Nuovo contratto
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white border border-[#E8E2D9] rounded-xl p-3">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[11px] uppercase tracking-wider text-[#635F59] font-semibold">Categoria</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="mt-1 h-9" data-testid="contract-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[11px] uppercase tracking-wider text-[#635F59] font-semibold">Stato</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="mt-1 h-9" data-testid="contract-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="active">Attivi</SelectItem>
              <SelectItem value="expired">Scaduti</SelectItem>
              <SelectItem value="archived">Archiviati</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Contract list */}
      {loading ? (
        <div className="text-center text-[#635F59] py-10">Caricamento…</div>
      ) : contracts.length === 0 ? (
        <div className="text-center text-[#635F59] py-12 border border-dashed border-[#E8E2D9] rounded-xl">
          <FileText className="w-10 h-10 mx-auto mb-3 text-[#E8E2D9]" />
          <p className="text-sm">Nessun contratto presente con questi filtri.</p>
          <Button onClick={openCreate} variant="outline" className="mt-4">
            <Plus className="w-4 h-4 mr-2" />Carica il primo contratto
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {contracts.map((c) => (
            <div key={c.id} className="bg-white border border-[#E8E2D9] rounded-xl p-4 hover:shadow-md transition" data-testid={`contract-${c.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <Badge variant="outline" className={getCategoryStyle(c.category)}>{getCategoryLabel(c.category)}</Badge>
                    {c.is_expired && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300"><AlertCircle className="w-3 h-3 mr-1" />Scaduto</Badge>}
                    {c.linked_deadline_id && <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-300"><CalendarIcon className="w-3 h-3 mr-1" />Scadenza attiva</Badge>}
                    {c.status === "archived" && <Badge variant="outline" className="bg-slate-100 text-slate-700"><Archive className="w-3 h-3 mr-1" />Archiviato</Badge>}
                  </div>
                  <h3 className="font-semibold text-[#1E3A5F] text-base">{c.title}</h3>
                  {c.counterparty && <p className="text-xs text-[#635F59] mt-0.5">Controparte: <span className="font-medium text-[#2D2926]">{c.counterparty}</span></p>}
                  <div className="flex flex-wrap gap-4 mt-2 text-xs text-[#635F59]">
                    {c.start_date && <span><strong>Inizio:</strong> {c.start_date}</span>}
                    {c.end_date && <span><strong>Fine:</strong> {c.end_date}</span>}
                    {c.amount != null && <span><strong>Importo:</strong> €{c.amount.toLocaleString("it-IT")}</span>}
                  </div>
                  {c.notes && <p className="text-xs text-[#635F59] mt-2 italic">{c.notes}</p>}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {c.file_url && (
                    <a
                      href={c.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#1E3A5F] text-white rounded hover:bg-[#152A45] transition"
                      data-testid={`contract-open-${c.id}`}
                    >
                      <ExternalLink className="w-3 h-3" />Apri file
                    </a>
                  )}
                  <div className="flex gap-1">
                    <Button onClick={() => openEdit(c)} variant="outline" size="sm" data-testid={`contract-edit-${c.id}`}>
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    {c.status !== "archived" && (
                      <Button onClick={() => handleArchive(c)} variant="outline" size="sm" data-testid={`contract-archive-${c.id}`} title="Archivia">
                        <Archive className="w-3 h-3" />
                      </Button>
                    )}
                    <Button onClick={() => handleDelete(c)} variant="outline" size="sm" className="text-rose-700 hover:bg-rose-50" data-testid={`contract-delete-${c.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#AC5D45]" />
              {form.id ? "Modifica contratto" : "Nuovo contratto"}
            </DialogTitle>
            <DialogDescription>
              Carica un PDF dal tuo PC, oppure incolla il link da Google Drive, DropBox, OneDrive o qualsiasi URL pubblico.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Titolo *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="es. Contratto locazione 2026-2030"
                data-testid="contract-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="contract-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Controparte</Label>
                <Input
                  value={form.counterparty}
                  onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
                  placeholder="es. Mario Rossi · Enel · Studio ABC"
                  data-testid="contract-counterparty"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Data inizio</Label>
                <DateInputIT value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="contract-start-date" />
              </div>
              <div>
                <Label className="text-xs">Data fine / scadenza</Label>
                <DateInputIT value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="contract-end-date" />
              </div>
              <div>
                <Label className="text-xs">Importo (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="opzionale"
                  data-testid="contract-amount"
                />
              </div>
            </div>

            {/* File source — Upload OR Link */}
            <div className="border-t border-[#E8E2D9] pt-4">
              <Label className="text-xs uppercase tracking-wider text-[#635F59] font-semibold">Documento *</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                <label className="flex items-center justify-center gap-2 h-10 px-4 rounded-md cursor-pointer bg-[#1E3A5F] text-white hover:bg-[#152A45] transition text-sm font-semibold">
                  <input
                    type="file"
                    accept="application/pdf,.doc,.docx,image/*"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    data-testid="contract-file-input"
                  />
                  <Upload className="w-4 h-4" />
                  {uploading ? "Caricamento..." : "Carica file dal PC"}
                </label>
                <a
                  href="https://drive.google.com/drive/my-drive"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-white border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#FDFBF7] transition text-sm font-semibold"
                  data-testid="contract-open-gdrive"
                >
                  <svg className="w-4 h-4" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
                    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
                    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
                  </svg>
                  Apri Google Drive
                </a>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-[#635F59] my-1">
                <div className="flex-1 h-px bg-[#E8E2D9]" />
                <span>oppure incolla un link</span>
                <div className="flex-1 h-px bg-[#E8E2D9]" />
              </div>
              <div className="flex gap-2">
                <LinkIcon className="w-4 h-4 mt-2.5 shrink-0 text-[#635F59]" />
                <Input
                  value={form.file_url}
                  onChange={(e) => setForm({ ...form, file_url: e.target.value, file_name: form.file_name || "Link esterno" })}
                  placeholder="https://drive.google.com/file/d/... oppure https://dropbox.com/..."
                  data-testid="contract-file-url"
                />
              </div>
              {form.file_name && (
                <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />File: <span className="font-mono">{form.file_name}</span>
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs">Note</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Note interne, condizioni particolari, riferimenti..."
                rows={2}
                data-testid="contract-notes"
              />
            </div>

            <label className="flex items-start gap-2 text-xs text-[#635F59] cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_create_deadline}
                onChange={(e) => setForm({ ...form, auto_create_deadline: e.target.checked })}
                className="mt-0.5"
                data-testid="contract-auto-deadline"
              />
              <span>
                <strong>Crea scadenza automatica</strong> 30 giorni prima della data fine (visibile in Adempimenti → Scadenze).
                {!form.end_date && <span className="block text-amber-700 mt-1">⚠️ Inserisci una data fine per attivare il promemoria.</span>}
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="contract-save">
              {form.id ? "Salva modifiche" : "Crea contratto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
